import React from 'react';
import { Text, View } from '@react-pdf/renderer';

const EMPTY_QUILL = '<p><br></p>';
const IS_HTML_RE = /<[a-z][\s\S]*>/i;
const INDENT_PER_LEVEL = 15; // points per Quill indent level

// Returns the raw ql-indent level (0 = no indent)
const getIndentLevel = (el) => {
  const match = el.className && el.className.match(/ql-indent-(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Returns the marginLeft in points for a given element
const getIndentMargin = (el) => getIndentLevel(el) * INDENT_PER_LEVEL;

// Converts a number to lowercase roman numerals (i, ii, iii …)
const toRoman = (n) => {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ['m', 'cm', 'd', 'cd', 'c', 'xc', 'l', 'xl', 'x', 'ix', 'v', 'iv', 'i'];
  let result = '';
  vals.forEach((v, i) => {
    while (n >= v) { result += syms[i]; n -= v; }
  });
  return result;
};

// Bullet label for ordered lists: 1. / a. / i. / 1. (cycles every 3 levels)
const orderedBullet = (level, counter) => {
  switch (level % 3) {
    case 0: return `${counter}.`;
    case 1: return `${String.fromCharCode(96 + counter)}.`;
    case 2: return `${toRoman(counter)}.`;
    default: return `${counter}.`;
  }
};

// Bullet symbol for unordered lists – use • for all levels to match the editor
const unorderedBullet = () => '\u2022';

// ---------------------------------------------------------------------------
// Inline renderer – returns nested <Text> elements for bold/italic/etc.
// `baseStyle` carries accumulated text styles down the tree.
// ---------------------------------------------------------------------------
const renderInline = (node, baseStyle, key) => {
  if (node.nodeType === 3) {
    const text = node.textContent;
    if (!text) return null;
    return (
      <Text key={key} style={baseStyle}>
        {text}
      </Text>
    );
  }

  if (node.nodeType !== 1) return null;

  const tag = node.tagName.toLowerCase();

  if (tag === 'br') {
    return <Text key={key} style={baseStyle}>{'\n'}</Text>;
  }

  let style = { ...baseStyle };
  if (tag === 'strong' || tag === 'b') style.fontWeight = 'bold';
  if (tag === 'em' || tag === 'i') style.fontStyle = 'italic';
  if (tag === 'u') style.textDecoration = 'underline';
  if (tag === 's') style.textDecoration = 'line-through';
  if (tag === 'a') style.color = '#0066cc';

  const children = Array.from(node.childNodes)
    .map((child, i) => renderInline(child, style, `${key}-${i}`))
    .filter(Boolean);

  return children.length > 0 ? (
    <React.Fragment key={key}>{children}</React.Fragment>
  ) : null;
};

// ---------------------------------------------------------------------------
// Block renderer – returns <Text> / <View> block-level elements.
// ---------------------------------------------------------------------------
const renderBlock = (node, textStyle, layoutStyles, key) => {
  if (node.nodeType === 3) {
    const text = node.textContent?.trim();
    return text ? (
      <Text key={key} style={textStyle}>
        {text}
      </Text>
    ) : null;
  }

  if (node.nodeType !== 1) return null;

  const tag = node.tagName.toLowerCase();

  const inlineChildren = (n) => {
    const isEmptyBr =
      n.childNodes.length === 1 && n.childNodes[0].tagName?.toLowerCase() === 'br';
    if (isEmptyBr) return [<Text key="br" style={textStyle}>{' '}</Text>];
    return Array.from(n.childNodes)
      .map((child, i) => renderInline(child, textStyle, `${key}-inline-${i}`))
      .filter(Boolean);
  };

  switch (tag) {
    case 'p': {
      const indent = getIndentMargin(node);
      return (
        <Text key={key} style={[textStyle, layoutStyles.p, indent ? { marginLeft: indent } : null]}>
          {inlineChildren(node)}
        </Text>
      );
    }

    case 'h1': {
      const indent = getIndentMargin(node);
      return (
        <Text key={key} style={[textStyle, layoutStyles.h1, indent ? { marginLeft: indent } : null]}>
          {inlineChildren(node)}
        </Text>
      );
    }

    case 'h2': {
      const indent = getIndentMargin(node);
      return (
        <Text key={key} style={[textStyle, layoutStyles.h2, indent ? { marginLeft: indent } : null]}>
          {inlineChildren(node)}
        </Text>
      );
    }

    case 'h3': {
      const indent = getIndentMargin(node);
      return (
        <Text key={key} style={[textStyle, layoutStyles.h3, indent ? { marginLeft: indent } : null]}>
          {inlineChildren(node)}
        </Text>
      );
    }

    case 'ul':
    case 'ol': {
      const isOrdered = tag === 'ol';
      const items = Array.from(node.children).filter(
        (el) => el.tagName.toLowerCase() === 'li',
      );

      // Per-level counters; reset deeper levels when stepping back out
      const counters = {};
      let prevLevel = -1;

      return (
        <View key={key} style={layoutStyles.list}>
          {items.map((li, i) => {
            const level = getIndentLevel(li);
            const indent = BASE_LIST_INDENT + level * INDENT_PER_LEVEL;

            // Reset counters for all levels deeper than current
            if (level < prevLevel) {
              Object.keys(counters).forEach((k) => {
                if (parseInt(k, 10) > level) delete counters[k];
              });
            }
            counters[level] = (counters[level] || 0) + 1;
            prevLevel = level;

            const bullet = isOrdered
              ? orderedBullet(level, counters[level])
              : unorderedBullet(level);

            const content = Array.from(li.childNodes)
              .map((child, j) => renderInline(child, textStyle, `${key}-li${i}-${j}`))
              .filter(Boolean);

            return (
              <View
                key={`${key}-li-${i}`}
                style={[layoutStyles.listRow, { marginLeft: indent }]}
              >
                <Text style={[textStyle, layoutStyles.listBullet, !isOrdered ? { fontSize: (textStyle.fontSize || 10) + 2 } : null]}>{bullet}</Text>
                <Text style={[textStyle, layoutStyles.listContent]}>{content}</Text>
              </View>
            );
          })}
        </View>
      );
    }

    default: {
      const children = Array.from(node.childNodes)
        .map((child, i) => renderBlock(child, textStyle, layoutStyles, `${key}-${i}`))
        .filter(Boolean);
      return children.length > 0 ? (
        <React.Fragment key={key}>{children}</React.Fragment>
      ) : null;
    }
  }
};

// ---------------------------------------------------------------------------
// Default layout styles
// ---------------------------------------------------------------------------
const BASE_LIST_INDENT = 15; // base left margin applied to every list item (level 0)

const DEFAULT_LAYOUT = {
  p: { marginBottom: 3 },
  h1: { fontSize: 14, fontWeight: 'bold', marginBottom: 5 },
  h2: { fontSize: 12, fontWeight: 'bold', marginBottom: 4 },
  h3: { fontSize: 11, fontWeight: 'bold', marginBottom: 3 },
  list: { marginBottom: 3 },
  listRow: { flexDirection: 'row', marginBottom: 2 },
  listBullet: { width: 12 },
  listContent: { flex: 1 },
};

// ---------------------------------------------------------------------------
// Public component
//
// Props:
//   html        – HTML string from Quill (or plain text for legacy data)
//   style       – styles applied to the outer <View> container (e.g. marginTop)
//   textStyle   – base text style for every <Text> node inside
//                 (font family, size, line-height, color …)
//                 Defaults to fontSize 10 / lineHeight 1.5 to match pdf body text.
// ---------------------------------------------------------------------------
export const QuillContentPdf = ({ html, style, textStyle }) => {
  const resolvedTextStyle = { fontSize: 10, lineHeight: 1.5, ...textStyle };

  if (!html || html === EMPTY_QUILL) {
    return null;
  }

  // Legacy plain-text content (stored before rich-text editor was introduced)
  if (!IS_HTML_RE.test(html)) {
    return (
      <View style={style}>
        <Text style={resolvedTextStyle}>{html}</Text>
      </View>
    );
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const blocks = Array.from(doc.body.childNodes)
    .map((node, i) => renderBlock(node, resolvedTextStyle, DEFAULT_LAYOUT, `b${i}`))
    .filter(Boolean);

  return <View style={style}>{blocks}</View>;
};

export default QuillContentPdf;
